import { type ReactElement } from 'react';
import {
  TableV2ActionsHeaderIcon,
  TableV2HeaderLabel,
  TableV2SortableHeader,
  tableV2ActionsColumnWidth,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';

export type SortableNestedSuiteRunField =
  | 'durationMs'
  | 'runNumber'
  | 'status'
  | 'tests'
  | 'timestamp';
export type SortableNestedTestRunField = 'durationMs' | 'runNumber' | 'status' | 'timestamp';

export const buildSuiteRunHeader = ({
  includeActions = true,
  sort,
  sortingDisabled,
  onSortChange,
}: {
  includeActions?: boolean;
  sort: ApiSort;
  sortingDisabled: boolean;
  onSortChange: (field: SortableNestedSuiteRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="runNumber"
        label="Suite Run"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="tests"
        label="Pass Rate"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="durationMs"
        label="Duration"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="timestamp"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    {includeActions ? (
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    ) : null}
  </TableRow>
);

export const buildTestRunHeader = ({
  includeActions = true,
  sort,
  sortingDisabled,
  onSortChange,
}: {
  includeActions?: boolean;
  sort: ApiSort;
  sortingDisabled: boolean;
  onSortChange: (field: SortableNestedTestRunField) => void;
}): ReactElement => (
  <TableRow>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="runNumber"
        label="Test Run"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="status"
        label="Status"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="durationMs"
        label="Duration"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2SortableHeader
        activeSort={sort}
        disabled={sortingDisabled}
        field="timestamp"
        label="Ran At"
        onSortChange={onSortChange}
      />
    </TableCell>
    <TableCell>
      <TableV2HeaderLabel label="Tags" />
    </TableCell>
    {includeActions ? (
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    ) : null}
  </TableRow>
);
